import { convertToRealValue } from "@/utils/textUtilities";
import { merge, isPlainObject, cloneDeep } from "lodash";

import {
    latestVersionByLocale as latestVersionByLocaleForRefundPolicy,
    transform as transformDataForRefundPolicy,
    retransform as retransformDataForRefundPolicy,
    questionByLocale as refundPolicyQuestionsByLocale,
} from "@/utils/service/DocumentGeneration/refundPolicy/index";

import {
    latestVersionByLocale as latestVersionByLocaleForPrivacyPolicy,
    transform as transformDataForPrivacyPolicy,
    retransform as retransformDataForPrivacyPolicy,
    questionByLocale as privacyQuestionsByLocale,
} from "@/utils/service/DocumentGeneration/privacyPolicy/index";

import {
    latestVersionByLocale as latestVersionByLocaleForTos,
    transform as transformDataForWebsiteTos,
    retransform as retransformDataForWebsiteTos,
    questionByLocale as tosQuestionsByLocale,
} from "@/utils/service/DocumentGeneration/websiteTos/index";

import {
    latestVersionByLocale as latestVersionByLocaleForCookiePolicy,
    transform as transformDataForCookiesPolicy,
    retransform as retransformDataForCookiesPolicy,
    questionByLocale as cookiePolicyQuestionsByLocale,
} from "@/utils/service/DocumentGeneration/cookiePolicy/index";

import {
    latestVersionByLocale as latestVersionByLocaleForEula,
    transform as transformDataForEula,
    retransform as retransformDataForEula,
    questionByLocale as eulaQuestionsByLocale,
} from "@/utils/service/DocumentGeneration/desktopEula/index";

import {
    latestVersionByLocale as latestVersionByLocaleForMobileEula,
    transform as transformDataForMobileEula,
    retransform as retransformDataForMobileEula,
    questionByLocale as mobileEulaQuestionsByLocale,
} from "@/utils/service/DocumentGeneration/mobileEula/index";

export const documentVersionByType = (type, locale = "us") => {
    switch (type) {
        case "website_tos":
            return latestVersionByLocaleForTos(locale);
        case "privacy_policy":
            return latestVersionByLocaleForPrivacyPolicy(locale);
        case "cookie_policy":
            return latestVersionByLocaleForCookiePolicy(locale);
        case "refund_policy":
            return latestVersionByLocaleForRefundPolicy(locale);
        case "desktop_eula":
            return latestVersionByLocaleForEula(locale);
        case "mobile_eula":
            return latestVersionByLocaleForMobileEula(locale);
        default:
            return "v1"
    }
};

export function questionByType(type, locale = "us", version) {
    switch (type) {
        case "website_tos":
            return tosQuestionsByLocale(locale, version);
        case "privacy_policy":
            return privacyQuestionsByLocale(locale, version);
        case "cookie_policy":
            return cookiePolicyQuestionsByLocale(locale, version);
        case "refund_policy":
            return refundPolicyQuestionsByLocale(locale, version);
        case "desktop_eula":
            return eulaQuestionsByLocale(locale, version);
        case "mobile_eula":
            return mobileEulaQuestionsByLocale(locale);
    }
    return null;
}

export function transformDataByType(type, model, locale = "us", version) {
    switch (type) {
        case "website_tos":
            return transformDataForWebsiteTos(
                modelPrep(model),
                locale,
                version
            );
        case "privacy_policy":
            return transformDataForPrivacyPolicy(
                modelPrep(model),
                locale,
                version
            );
        case "cookie_policy":
            return transformDataForCookiesPolicy(
                modelPrep(model),
                locale,
                version
            );
        case "refund_policy":
            return transformDataForRefundPolicy(
                modelPrep(model),
                locale,
                version
            );
        case "desktop_eula":
            return transformDataForEula(modelPrep(model), locale, version);
        case "mobile_eula":
            return transformDataForMobileEula(
                modelPrep(model),
                locale,
                version
            );
    }
}

export function retransformDataByType(type, params, locale = "us", version) {
    const model = cloneDeep(params);

    switch (type) {
        case "website_tos":
            return modelConvertForEdit(
                retransformDataForWebsiteTos(model, locale, version)
            );
        case "privacy_policy":
            return modelConvertForEdit(
                retransformDataForPrivacyPolicy(model, locale, version),
                [
                    "direct_marketing.unsubscribe",
                    "automatically_collected_data.other",
                    "cookies.types",
                    "unsubscribe.options",
                    "retention.main",
                ]
            );
        case "cookie_policy":
            return modelConvertForEdit(
                retransformDataForCookiesPolicy(model, locale, version),
                ["used_cookies"]
            );
        case "refund_policy":
            return modelConvertForEdit(
                retransformDataForRefundPolicy(model, locale, version),
                ["refund_process_return_address"]
            );
        case "desktop_eula":
            return modelConvertForEdit(
                retransformDataForEula(model, locale, version)
            );
        case "mobile_eula":
            return modelConvertForEdit(
                retransformDataForMobileEula(model, locale, version)
            );
    }
}

function modelPrep(model) {
    let final = {};
    Object.keys(model).forEach((key) => {
        if (key === "payment_processor") return;
        let value = convertToRealValue(model[key]);
        key.split(".")
            .reverse()
            .forEach((subKey) => {
                value = { [subKey]: value };
            });
        final = merge(final, value);
    });
    return final;
}

function modelConvertForEdit(params, keysToIgnore = []) {
    function valueTransform(value) {
        switch (typeof value) {
            case "boolean":
                return String(value);
            default:
                return value;
        }
    }

    function valueToModel(newValue, existingKey = "") {
        let final = {};
        Object.entries(newValue).forEach(([key, value]) => {
            let finalKey = (existingKey ? `${existingKey}.` : "") + key;

            if (isPlainObject(value) && !keysToIgnore.includes(finalKey)) {
                Object.assign(final, valueToModel(value, finalKey));
            } else {
                Object.assign(final, { [finalKey]: valueTransform(value) });
            }
        });

        return final;
    }

    return valueToModel(params, "");
}

export function isValueDefaultValueOfQuestion(value, key, questions) {
    //console.group("isValueDefaultValueOfQuestion", value, key);

    // find Question input
    let questionInput = null;

    questions.find(({ inputs }) => {
        //console.log('inputs', inputs);
        questionInput = inputs.find(({ model }) => {
            //console.log("inputs model", {model, key, res: model === key});
            return model === key;
        });
        return !!questionInput;
    });

    //console.warn("Found questionInput", questionInput);

    let result = false;
    if (Array.isArray(questionInput.values)) {
        //console.log('Search value - Array', {vs: questionInput.values, value});
        result = !!questionInput.values.find((v) => v === value);
    } else {
        //console.log('Search value - Object', {vs: Object.keys(questionInput.values), value});
        result = !!Object.keys(questionInput.values).find((v) => v === value);
    }

    //console.groupEnd();

    return result;
}

export const getMissingQuestions = (questions, models = []) => {
    return models
        .map((model) =>
            questions.find(({ inputs }) =>
                inputs.find((input) => input.model === model)
            )
        )
        .filter((q) => q);
};
